import { useCallback, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import {
  UploadCloudIcon,
  FileTextIcon,
  AlertCircleIcon,
  FolderIcon,
  SparklesIcon,
  BrainCircuitIcon,
} from 'lucide-react'
import { uploadDocument } from '../utils/api'
import { Document } from '../types'

interface DocumentUploadProps {
  onUploadSuccess: (document: Document) => void
  onViewDocuments: () => void
  documentCount: number
}

export const DocumentUpload = ({
  onUploadSuccess,
  onViewDocuments,
  documentCount,
}: DocumentUploadProps) => {
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [error, setError] = useState<string | null>(null)

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) return

      const file = acceptedFiles[0]
      if (file.type !== 'application/pdf') {
        setError('Only PDF files are supported')
        return
      }

      setIsUploading(true)
      setError(null)

      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 95) {
            clearInterval(progressInterval)
            return 95
          }
          return prev + 5
        })
      }, 100)

      try {
        const document = await uploadDocument(file)
        clearInterval(progressInterval)
        setUploadProgress(100)
        setTimeout(() => {
          setIsUploading(false)
          setUploadProgress(0)
          onUploadSuccess(document)
        }, 500)
      } catch (err: any) {
        clearInterval(progressInterval)
        setError(err.message || 'Failed to upload document. Please try again.')
        setIsUploading(false)
        setUploadProgress(0)
      }
    },
    [onUploadSuccess],
  )


  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
    },
    maxFiles: 1,
    disabled: isUploading,
  })

  return (
    <div className="flex flex-col items-center justify-center w-full h-full py-12">
      <div className="text-center mb-8">
        <div className="flex items-center justify-center mb-4">
          <div className="relative">
            <FileTextIcon className="h-16 w-16 text-blue-600" />
            <BrainCircuitIcon className="h-8 w-8 text-purple-500 absolute -top-2 -right-2" />
            <SparklesIcon className="h-6 w-6 text-yellow-500 absolute -bottom-1 -left-1 animate-pulse" />
          </div>
        </div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Smart IEP Document Analysis
        </h1>
        <p className="text-gray-600 text-center max-w-2xl">
          Upload an Individualized Education Program (IEP) document to analyze its
          content with our intelligent system. Our AI adapts to any
          IEP structure and presents information with smart visual organization.
        </p>
      </div>

      <div className="w-full max-w-2xl">
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
            isDragActive
              ? 'border-blue-500 bg-gradient-to-br from-blue-50 to-purple-50'
              : 'border-gray-300 hover:border-blue-400 hover:bg-gradient-to-br hover:from-blue-50 hover:to-purple-50'
          } ${isUploading ? 'opacity-75' : ''}`}
        >
          <input {...getInputProps()} />
          {isUploading ? (
            <div className="flex flex-col items-center">
              <div className="relative mb-4">
                <UploadCloudIcon className="h-12 w-12 text-blue-500" />
                <SparklesIcon className="h-6 w-6 text-purple-500 absolute -top-1 -right-1 animate-spin" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-1">
                {uploadProgress < 100
                  ? 'Uploading document...'
                  : 'Processing with smart analysis...'}
              </h3>
              <div className="w-full max-w-md h-2 bg-gray-200 rounded-full overflow-hidden mt-2">
                <div
                  className="h-full bg-gradient-to-r from-blue-500 to-purple-500 transition-all duration-300 ease-out"
                  style={{
                    width: `${uploadProgress}%`,
                  }}
                />
              </div>
              <p className="text-sm text-gray-500 mt-2">
                {uploadProgress}% complete
              </p>
            </div>
          ) : (
            <div className="flex flex-col items-center">
              <div className="relative mb-4">
                <UploadCloudIcon className="h-12 w-12 text-blue-500" />
                <BrainCircuitIcon className="h-6 w-6 text-purple-500 absolute -top-1 -right-1" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-1">
                {isDragActive
                  ? 'Drop your PDF file here'
                  : 'Drag & drop your PDF file here'}
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                or click to browse files
              </p>
              <p className="text-xs text-gray-400">
                Only PDF files are supported (max 10MB)
              </p>
              {error && (
                <div className="mt-4 flex items-center text-red-600">
                  <AlertCircleIcon className="h-4 w-4 mr-1" />
                  <span className="text-sm">{error}</span>
                </div>
              )}
            </div>
          )}
        </div>


        {documentCount > 0 && (
          <div className="mt-6 flex justify-center">
            <button
              onClick={onViewDocuments}
              className="flex items-center px-4 py-2 text-sm font-medium text-blue-700 bg-blue-50 rounded-md hover:bg-blue-100"
            >
              <FolderIcon className="h-4 w-4 mr-1" />
              View your documents ({documentCount})
            </button>
          </div>
        )}

        <div className="mt-8 bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
            <SparklesIcon className="h-5 w-5 text-purple-500 mr-2" />
            Smart Analysis Features
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-start">
              <div className="flex-shrink-0 flex items-center justify-center h-6 w-6 rounded-full bg-blue-100 text-blue-600 mr-3">
                1
              </div>
              <div>
                <p className="font-medium text-gray-900">Adaptive Analysis</p>
                <p className="text-sm text-gray-600">
                  Adapts to any IEP structure or format
                </p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="flex-shrink-0 flex items-center justify-center h-6 w-6 rounded-full bg-purple-100 text-purple-600 mr-3">
                2
              </div>
              <div>
                <p className="font-medium text-gray-900">Intelligent Rendering</p>
                <p className="text-sm text-gray-600">
                  Smart visual organization with semantic recognition
                </p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="flex-shrink-0 flex items-center justify-center h-6 w-6 rounded-full bg-green-100 text-green-600 mr-3">
                3
              </div>
              <div>
                <p className="font-medium text-gray-900">Flexible Analysis</p>
                <p className="text-sm text-gray-600">
                  Handles various data structures gracefully
                </p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="flex-shrink-0 flex items-center justify-center h-6 w-6 rounded-full bg-yellow-100 text-yellow-600 mr-3">
                4
              </div>
              <div>
                <p className="font-medium text-gray-900">Source Integration</p>
                <p className="text-sm text-gray-600">
                  Links back to original document pages
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}